import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function NestedRoutesPage(): Node {
  return html`
    <div>
      <h1>Nested Routes</h1>
      <p>Parent layouts can render child routes via the <code>outlet</code> parameter. This lets you share layout elements (navbars, sidebars) across related pages.</p>

      <h2>How It Works</h2>
      <p>When a route has <code>children</code>, its view function receives a second argument — the <code>outlet</code>. The outlet is the rendered child route's Node. Place it wherever you want the nested content to appear.</p>

      <h2>Example: Settings Layout</h2>
      ${CodeBlock(`import { Router, Link, html, mount } from 'onefold';

function SettingsLayout(params: any, outlet: Node): Node {
  return html\`
    <div class="settings">
      <nav class="settings-nav">
        \${Link('/settings/profile', html\`<span>Profile</span>\`)}
        \${Link('/settings/billing', html\`<span>Billing</span>\`)}
        \${Link('/settings/notifications', html\`<span>Notifications</span>\`)}
      </nav>
      <div class="settings-content">
        \${outlet}
      </div>
    </div>
  \`;
}

function ProfilePage(): Node {
  return html\`<h2>Profile Settings</h2>\`;
}

function BillingPage(): Node {
  return html\`<h2>Billing & Subscription</h2>\`;
}

function NotificationsPage(): Node {
  return html\`<h2>Notification Preferences</h2>\`;
}

const App = Router([
  { path: '/settings', view: SettingsLayout, children: [
    { path: '/profile', view: () => ProfilePage() },
    { path: '/billing', view: () => BillingPage() },
    { path: '/notifications', view: () => NotificationsPage() },
  ]},
]);`)}

      ${Callout('Child paths are relative to the parent. /settings/profile matches the parent /settings and then the child /profile.')}

      <h2>Multiple Nesting Levels</h2>
      <p>Nesting can go as deep as needed. Each level receives its own outlet:</p>
      ${CodeBlock(`const routes = [
  { path: '/app', view: AppLayout, children: [
    { path: '/dashboard', view: DashboardLayout, children: [
      { path: '/stats', view: () => StatsPage() },
      { path: '/charts', view: () => ChartsPage() },
    ]},
    { path: '/settings', view: SettingsLayout, children: [
      { path: '/profile', view: () => ProfilePage() },
    ]},
  ]},
];`)}

      <h2>Index Routes</h2>
      <p>Use an empty child path to define a default view for a parent route:</p>
      ${CodeBlock(`{ path: '/settings', view: SettingsLayout, children: [
  { path: '', view: () => SettingsOverview() },  // /settings
  { path: '/profile', view: () => ProfilePage() }, // /settings/profile
]}`)}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/routing/link">Link</a> — declarative navigation with active state</li>
        <li><a href="/routing/params">Dynamic Params</a> — capture URL segments as parameters</li>
      </ul>
    </div>
  `;
}
